import { useEffect, useId, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { isToday, isTomorrow } from 'date-fns';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  useCarousel,
} from '@/components/ui/carousel';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type { SchedulingSlot } from '@/types/scheduling.type';

type DateCarouselProps = {
  selectedDate: Date | undefined;
  slotsData: SchedulingSlot[] | undefined;
  onDateSelect: (date: Date) => void;
};

export function DateCarousel({ selectedDate, slotsData, onDateSelect }: DateCarouselProps) {
  const dateCarouselOptions = getDateCarouselOptions(slotsData);
  const timeZoneAbbr = getTimeZoneAbbr();
  const carouselContentId = useId();
  const [api, setApi] = useState<CarouselApi>();

  useEffect(
    function scrollToSelectedDateOnChange() {
      if (!api || !selectedDate) return;

      const selectedDateKey = format(selectedDate, 'yyyy-MM-dd');
      const selectedIndex = dateCarouselOptions.findIndex(
        (option) => format(option.date, 'yyyy-MM-dd') === selectedDateKey
      );

      if (selectedIndex !== -1) {
        api.scrollTo(selectedIndex); // Smooth scroll by default
      }
    },
    [api, selectedDate, dateCarouselOptions]
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Carousel
          className="-m-2"
          plugins={[WheelGesturesPlugin()]}
          opts={{
            slidesToScroll: 1,
            align: 'start',
            containScroll: 'trimSnaps',
          }}
          setApi={setApi}
        >
          <CarouselContent id={carouselContentId} className="m-2 flex gap-2">
            {dateCarouselOptions.map((option) => {
              const dateKey = format(option.date, 'yyyy-MM-dd');
              const hasSlots = slotsData?.some((slot) => slot.date === dateKey) ?? false;
              const isSelected =
                (selectedDate && format(selectedDate, 'yyyy-MM-dd') === dateKey) ?? false;

              return (
                <CarouselItem key={dateKey} className="flex-none pl-0">
                  <Button
                    type="button"
                    disabled={!hasSlots}
                    variant="outline"
                    onClick={() => onDateSelect(option.date)}
                    aria-label={`Select ${option.label}, ${option.shortLabel}`}
                    aria-pressed={isSelected}
                    className={cn(
                      'flex h-[62px] w-full flex-col items-start justify-center gap-1 rounded-md border px-3 py-3 transition-all sm:p-3',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      'border-[#DAE2F6] bg-white text-main hover:bg-accent',
                      isSelected &&
                        'border-hydrangea-500 bg-hydrangea-500 text-white shadow-sm hover:bg-hydrangea-500 hover:text-white'
                    )}
                  >
                    <span>{option.label}</span>
                    <span className="text-lg leading-none">{option.shortLabel}</span>
                  </Button>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <ScrollIndicators carouselContentId={carouselContentId} />
        </Carousel>
      </div>
      <p className="text-sm text-muted-foreground italic">
        Time displayed in local time zone {timeZoneAbbr && `(${timeZoneAbbr})`}
      </p>
      <Separator className="bg-[#DAE2F6]" />
    </div>
  );
}

type DateCarouselOption = {
  date: Date;
  label: string;
  shortLabel: string;
};

function getDateCarouselOptions(slotsData: SchedulingSlot[] | undefined): DateCarouselOption[] {
  if (!slotsData || slotsData.length === 0) return [];

  return slotsData.map((slotData) => {
    const date = parseISO(`${slotData.date}T00:00:00`);
    let label = '';
    let shortLabel = '';

    if (isToday(date)) {
      label = 'Today';
      shortLabel = format(date, 'EEE, MMM d');
    } else if (isTomorrow(date)) {
      label = 'Tomorrow';
      shortLabel = format(date, 'EEE, MMM d');
    } else {
      label = format(date, 'EEEE');
      shortLabel = format(date, 'EEE, MMM d');
    }

    return { date, label, shortLabel };
  });
}

const getTimeZoneAbbr = () => {
  return new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
  })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName')?.value;
};

type ScrollIndicatorsProps = {
  carouselContentId: string;
};

function ScrollIndicators({ carouselContentId }: ScrollIndicatorsProps) {
  const { canScrollPrev, canScrollNext, scrollPrev, scrollNext } = useCarousel();

  return (
    <nav aria-label="Date carousel navigation" className="pointer-events-none absolute inset-0">
      {canScrollPrev && (
        <>
          <div
            className="pointer-events-none absolute top-0 -left-2 z-10 h-full w-12 bg-linear-to-r from-background to-transparent"
            aria-hidden="true"
          />
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            className="pointer-events-auto absolute top-1/2 -left-2.5 z-20 -translate-y-1/2 border-hydrangea-100 shadow-md sm:-left-12"
            onClick={scrollPrev}
            aria-label="Scroll to previous dates"
            aria-controls={carouselContentId}
            aria-keyshortcuts="ArrowLeft"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </Button>
        </>
      )}

      {canScrollNext && (
        <>
          <div
            className="pointer-events-none absolute top-0 -right-2 z-10 h-full w-12 bg-linear-to-l from-background to-transparent"
            aria-hidden="true"
          />
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            className="pointer-events-auto absolute top-1/2 -right-2.5 z-20 -translate-y-1/2 border-hydrangea-100 shadow-md sm:-right-12"
            onClick={scrollNext}
            aria-label="Scroll to next dates"
            aria-controls={carouselContentId}
            aria-keyshortcuts="ArrowRight"
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </Button>
        </>
      )}
    </nav>
  );
}
